import React, { useEffect, useState } from 'react';
import './GuestPaymentPages.css';
import { fetchConfirmedBookingById } from '../utils/bookingStorage';
import { assertSupabase } from '../lib/supabase';
import { createCheckoutSession } from '../utils/checkoutClient';
import { generateApprovedBookingPDF } from '../utils/pdfGenerator';
import { getPublicSiteOrigin } from '../utils/siteUrl';

function bookingIdFromQuery(): string | null {
  const id = new URLSearchParams(window.location.search).get('bookingId');
  return id && id.trim() ? id.trim() : null;
}

export const PaymentPage: React.FC = () => {
  const bookingId = bookingIdFromQuery();
  const [error, setError] = useState<string | null>(
    bookingId ? null : 'Missing booking reference. Use the link from your confirmation email.'
  );
  const [loading, setLoading] = useState(!!bookingId);
  const [paying, setPaying] = useState(false);
  const [summary, setSummary] = useState<{
    guestName: string;
    campName: string;
    checkIn: string;
    checkOut: string;
    total: number;
    paidAt: string | null;
  } | null>(null);

  useEffect(() => {
    if (!bookingId) return;

    let cancelled = false;
    (async () => {
      setLoading(true);
      const { booking, error: err } = await fetchConfirmedBookingById(bookingId);
      if (cancelled) return;
      setLoading(false);
      if (!booking || err) {
        setError(err || 'We could not find an active booking for this link.');
        return;
      }
      setSummary({
        guestName: booking.guestName,
        campName: booking.campName,
        checkIn: booking.checkIn.toLocaleDateString(),
        checkOut: booking.checkOut.toLocaleDateString(),
        total: booking.totalPrice,
        paidAt: booking.paidAt ?? null,
      });
      setError(null);
    })();

    return () => {
      cancelled = true;
    };
  }, [bookingId]);

  const handlePay = async () => {
    if (!bookingId) return;
    setPaying(true);
    setError(null);
    const { url, error: checkoutErr } = await createCheckoutSession(bookingId);
    setPaying(false);
    if (checkoutErr) {
      setError(checkoutErr);
      return;
    }
    if (url) window.location.href = url;
  };

  if (!bookingId) {
    return (
      <div className="guest-flow-page">
        <div className="guest-flow-card">
          <h1>Pay for your booking</h1>
          <p className="guest-flow-error">{error}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="guest-flow-page">
        <div className="guest-flow-card">
          <div className="guest-flow-muted">Loading your booking…</div>
        </div>
      </div>
    );
  }

  if (error && !summary) {
    return (
      <div className="guest-flow-page">
        <div className="guest-flow-card">
          <h1>Pay for your booking</h1>
          <p className="guest-flow-error">{error}</p>
        </div>
      </div>
    );
  }

  if (summary?.paidAt) {
    return (
      <div className="guest-flow-page">
        <div className="guest-flow-card">
          <div className="guest-flow-success-icon">✓</div>
          <h1>Already paid</h1>
          <p className="guest-flow-intro">
            This booking was marked paid on {new Date(summary.paidAt).toLocaleString()}.
          </p>
          <dl className="guest-flow-details">
            <div>
              <dt>Guest</dt>
              <dd>{summary.guestName}</dd>
            </div>
            <div>
              <dt>Camp</dt>
              <dd>{summary.campName}</dd>
            </div>
            <div>
              <dt>Total</dt>
              <dd>${summary.total.toFixed(2)} AUD</dd>
            </div>
          </dl>
          <a className="guest-flow-link" href="/">
            Back to Southern Comfort Camps
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="guest-flow-page">
      <div className="guest-flow-card">
        <h1>Pay securely</h1>
        <p className="guest-flow-intro">
          You will complete payment through Stripe Checkout (test mode in development). Charges appear as Southern
          Comfort Camps.
        </p>
        {summary && (
          <dl className="guest-flow-details">
            <div>
              <dt>Guest</dt>
              <dd>{summary.guestName}</dd>
            </div>
            <div>
              <dt>Camp</dt>
              <dd>{summary.campName}</dd>
            </div>
            <div>
              <dt>Stay</dt>
              <dd>
                {summary.checkIn} – {summary.checkOut}
              </dd>
            </div>
            <div>
              <dt>Total</dt>
              <dd>${summary.total.toFixed(2)} AUD</dd>
            </div>
            <div>
              <dt>Booking ID</dt>
              <dd className="guest-flow-mono">{bookingId}</dd>
            </div>
          </dl>
        )}
        {error && <p className="guest-flow-error">{error}</p>}
        <button type="button" className="guest-flow-primary" onClick={handlePay} disabled={paying}>
          {paying ? 'Redirecting…' : 'Continue to card payment'}
        </button>
        <p className="guest-flow-footnote">
          Run the checkout server locally on port 4242 (<code>npm run server</code>) so this page can reach{' '}
          <code>/api/create-checkout-session</code>, or set <code>REACT_APP_CHECKOUT_API_URL</code> in production.
        </p>
      </div>
    </div>
  );
};

export const PaymentSuccessPage: React.FC = () => {
  const sessionId = new URLSearchParams(window.location.search).get('session_id');
  return (
    <div className="guest-flow-page">
      <div className="guest-flow-card">
        <div className="guest-flow-success-icon">✓</div>
        <h1>Payment received</h1>
        <p>Thank you. Stripe has processed your payment in test mode where applicable.</p>
        <p className="guest-flow-muted">
          Our server updates your booking when it receives Stripe&apos;s confirmation. If admin still shows unpaid,
          confirm the webhook endpoint is configured and reachable (see <code>STRIPE_WEBHOOK_SECRET</code>).
        </p>
        {sessionId && (
          <p className="guest-flow-muted">
            Reference: <span className="guest-flow-mono">{sessionId}</span>
          </p>
        )}
        <a className="guest-flow-link" href="/">
          Back to Southern Comfort Camps
        </a>
      </div>
    </div>
  );
};

export const PaymentCancelledPage: React.FC = () => (
  <div className="guest-flow-page">
    <div className="guest-flow-card">
      <h1>Checkout cancelled</h1>
      <p>No payment was taken. You can close this tab or try again using the payment link from your email.</p>
      <p className="guest-flow-muted">If something went wrong, contact info@southerncomfortcamps.com</p>
    </div>
  </div>
);

export const ConfirmationPdfPage: React.FC = () => {
  const bookingId = bookingIdFromQuery();
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [message, setMessage] = useState<string>('');

  useEffect(() => {
    if (!bookingId) {
      setStatus('error');
      setMessage('Missing booking reference.');
      return;
    }

    try {
      assertSupabase();
    } catch {
      setStatus('error');
      setMessage('Booking lookup is unavailable (Supabase not configured).');
      return;
    }

    let cancelled = false;
    void (async () => {
      const { booking, error: err } = await fetchConfirmedBookingById(bookingId);
      if (cancelled) return;
      if (!booking || err) {
        setStatus('error');
        setMessage(err || 'Confirmed booking not found.');
        return;
      }

      const origin = getPublicSiteOrigin();
      const paymentUrl = `${origin}/payment?bookingId=${encodeURIComponent(booking.id)}`;

      try {
        const doc = generateApprovedBookingPDF({
          bookingId: booking.id,
          guestName: booking.guestName,
          guestEmail: booking.guestEmail,
          guestPhone: booking.guestPhone,
          campName: booking.campName,
          checkIn: booking.checkIn,
          checkOut: booking.checkOut,
          guests: booking.guests,
          totalPrice: booking.totalPrice,
          paymentPageUrl: paymentUrl,
        });
        doc.save(`Southern-Comfort-Camps-confirmed-${booking.id}.pdf`);
      } catch (e) {
        console.error(e);
        setStatus('error');
        setMessage('Could not build the PDF. Try again later.');
        return;
      }

      setStatus('ready');
    })();

    return () => {
      cancelled = true;
    };
  }, [bookingId]);

  if (status === 'loading') {
    return (
      <div className="guest-flow-page">
        <div className="guest-flow-card">
          <h1>Confirmation PDF</h1>
          <p className="guest-flow-muted">Preparing your document…</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="guest-flow-page">
        <div className="guest-flow-card">
          <h1>Confirmation PDF</h1>
          <p className="guest-flow-error">{message}</p>
        </div>
      </div>
    );
  }

  if (!bookingId) return null;

  return (
    <div className="guest-flow-page">
      <div className="guest-flow-card">
        <div className="guest-flow-success-icon">✓</div>
        <h1>Download started</h1>
        <p>
          If your browser did not save the PDF, check your downloads folder or allow downloads for this site. The file
          includes a link to pay online.
        </p>
        <a className="guest-flow-primary guest-flow-inline-btn" href={`/payment?bookingId=${encodeURIComponent(bookingId)}`}>
          Open payment page
        </a>
      </div>
    </div>
  );
};
